/** Phase-0 shape inspection only. Callers must first verify every history row's
 * current-user, agent and conversation ownership. This is not a tool-schema parser.
 */
export interface CopilotMcpCatalogStructureTarget {
  serverSchemaName: string;
  toolName: "getJiraIssue";
}

const SERVER =
  /^[A-Za-z_][A-Za-z0-9_]{0,199}\.(?:topic|action)\.[A-Za-z_][A-Za-z0-9_]{0,199}$(?![\s\S])/;
const ROOT_KEYS = ["dialogSchemaName", "toolsList", "identifier", "schemaName", "$kind"] as const;
const ENTRY_KEYS = [
  "$kind",
  "identifier",
  "schemaName",
  "displayName",
  "description",
  "llmIdentifierPrefix",
  "inputs",
  "outputs",
  "inputSchema",
  "outputSchema",
  "propertyName",
  "type",
  "isRequired",
] as const;
const MAX_ROWS = 200;
const MAX_EVENTS = 5;
const MAX_TOOLS = 100;
const MAX_ENTRY_SHAPES = 20;
const MAX_KEYS = 100;
type RecordValue = Record<string, unknown>;
type TypeTag =
  "absent" | "null" | "object" | "array" | "string" | "number" | "boolean" | "unsupported";

function record(value: unknown): value is RecordValue {
  if (value === null || typeof value !== "object" || Array.isArray(value)) return false;
  try {
    const prototype: unknown = Object.getPrototypeOf(value);
    return prototype === Object.prototype || prototype === null;
  } catch {
    return false;
  }
}

/** Never invoke accessors or coerce untrusted values. Production inputs are JSON. */
function field(value: RecordValue, key: string): { tag: TypeTag; value?: unknown } {
  try {
    const descriptor = Object.getOwnPropertyDescriptor(value, key);
    if (!descriptor) return { tag: "absent" };
    if (!("value" in descriptor)) return { tag: "unsupported" };
    const item: unknown = descriptor.value;
    if (item === null) return { tag: "null", value: item };
    if (Array.isArray(item)) return { tag: "array", value: item };
    if (record(item)) return { tag: "object", value: item };
    if (typeof item === "string") return { tag: "string", value: item };
    if (typeof item === "boolean") return { tag: "boolean", value: item };
    if (typeof item === "number" && Number.isFinite(item)) return { tag: "number", value: item };
    return { tag: "unsupported" };
  } catch {
    return { tag: "unsupported" };
  }
}

function shape(value: RecordValue, keys: readonly string[]) {
  const fields = Object.fromEntries(keys.map((key) => [key, field(value, key).tag]));
  try {
    const names = Object.keys(value);
    return {
      fields,
      unknownKeyCount:
        names.length <= MAX_KEYS ? names.filter((key) => !keys.includes(key)).length : null,
      keyCountLimitExceeded: names.length > MAX_KEYS,
    };
  } catch {
    return { fields, unknownKeyCount: null, keyCountLimitExceeded: true };
  }
}

function comparisons(value: RecordValue, target: CopilotMcpCatalogStructureTarget) {
  const equals = (key: string, expected: string) => {
    const item = field(value, key);
    return item.tag === "string" ? item.value === expected : null;
  };
  return {
    identifierEqualsRequestedServer: equals("identifier", target.serverSchemaName),
    identifierEqualsRequestedTool: equals("identifier", target.toolName),
    schemaNameEqualsRequestedServer: equals("schemaName", target.serverSchemaName),
    schemaNameEqualsRequestedTool: equals("schemaName", target.toolName),
    dialogSchemaNameEqualsRequestedServer: equals("dialogSchemaName", target.serverSchemaName),
    kindEqualsDynamicServerToolDefinition: equals("$kind", "DynamicServerToolDefinition"),
  };
}

function eventShape(
  row: RecordValue,
  activityIndex: number,
  target: CopilotMcpCatalogStructureTarget,
) {
  const value = field(row, "value");
  if (value.tag !== "object")
    return { activityIndex, state: "unsupported-envelope" as const, valueType: value.tag };
  const envelope = value.value as RecordValue;
  const tools = field(envelope, "toolsList");
  const common = {
    activityIndex,
    state: "envelope-observed" as const,
    valueType: value.tag,
    ...shape(envelope, ROOT_KEYS),
    comparisons: comparisons(envelope, target),
  };
  if (tools.tag !== "array")
    return {
      ...common,
      toolsListState: tools.tag,
      entryCount: null,
      inspectedEntryCount: 0,
      omittedEntryCount: null,
      entryLimitExceeded: false,
      entries: [],
    };
  const entries = tools.value as unknown[];
  if (entries.length > MAX_TOOLS)
    return {
      ...common,
      toolsListState: "over-limit" as const,
      entryCount: null,
      inspectedEntryCount: 0,
      omittedEntryCount: null,
      entryLimitExceeded: true,
      entries: [],
    };
  const projected = entries.slice(0, MAX_ENTRY_SHAPES).map((entry, entryIndex) => {
    if (!record(entry)) return { entryIndex, state: "unsupported-entry" as const };
    return {
      entryIndex,
      state: "entry-observed" as const,
      ...shape(entry, ENTRY_KEYS),
      comparisons: comparisons(entry, target),
    };
  });
  return {
    ...common,
    toolsListState: "array" as const,
    entryCount: entries.length,
    inspectedEntryCount: projected.length,
    omittedEntryCount: entries.length - projected.length,
    entryLimitExceeded: false,
    entries: projected,
  };
}

/** No recursive discovery, schema interpretation, string lengths/hashes or values.
 * Literal comparisons only describe fixed fields, never verified tool identity.
 * Public Studio consumer: 317.39f73bdd module30089 uses value.dialogSchemaName
 * and value.toolsList; 140.2f987358 module25614 describes tool-definition keys.
 */
export function projectCopilotMcpCatalogStructure(
  rows: readonly unknown[],
  target: CopilotMcpCatalogStructureTarget,
) {
  const base = {
    experimental: true,
    evidenceKind: "recorded-conversation-catalog-envelope-structure" as const,
    targetIdentityVerified: false,
    limits: {
      historyRows: MAX_ROWS,
      catalogEvents: MAX_EVENTS,
      toolsPerEvent: MAX_TOOLS,
      entryShapesPerEvent: MAX_ENTRY_SHAPES,
    },
    notice:
      "Phase-0 structure from the first five bot-role catalog-named events in one already ownership-verified conversation page. Fixed key/type tags and literal equality flags are not verified server/tool identity, a tool schema, original MCP schema fidelity, current availability, consent, grants or execution proof. No nested input/output schemas, descriptions, defaults, examples, enum values, arbitrary keys, primitive values, string lengths, hashes or raw event payloads are returned. Missing and omitted metadata is not evidence of absence. No additional requests are performed by this projector.",
  };
  if (
    !record(target) ||
    field(target, "toolName").value !== "getJiraIssue" ||
    typeof field(target, "serverSchemaName").value !== "string" ||
    !SERVER.test(field(target, "serverSchemaName").value as string) ||
    !Array.isArray(rows) ||
    rows.length > MAX_ROWS
  ) {
    return { ...base, state: "unsupported-input" as const, events: [] };
  }
  let catalogEventCount = 0,
    botCatalogEventCount = 0,
    missingRoleCount = 0,
    unsupportedRoleCount = 0,
    otherRoleCount = 0;
  const events: ReturnType<typeof eventShape>[] = [];
  rows.forEach((row, activityIndex) => {
    if (
      !record(row) ||
      field(row, "type").value !== "event" ||
      field(row, "name").value !== "DynamicServerToolsList"
    )
      return;
    catalogEventCount++;
    const sender = field(row, "from");
    if (sender.tag === "absent" || sender.tag === "null") {
      missingRoleCount++;
      return;
    }
    if (sender.tag !== "object") {
      unsupportedRoleCount++;
      return;
    }
    const role = field(sender.value as RecordValue, "role");
    if (role.tag === "absent" || role.tag === "null") {
      missingRoleCount++;
      return;
    }
    if (role.tag !== "string" || (role.value !== "bot" && role.value !== "user")) {
      unsupportedRoleCount++;
      return;
    }
    if (role.value !== "bot") {
      otherRoleCount++;
      return;
    }
    botCatalogEventCount++;
    if (events.length < MAX_EVENTS) events.push(eventShape(row, activityIndex, target));
  });
  return {
    ...base,
    state: "projected" as const,
    inspectedHistoryRowCount: rows.length,
    catalogEventCount,
    botCatalogEventCount,
    missingRoleCount,
    unsupportedRoleCount,
    otherRoleCount,
    inspectedCatalogEventCount: events.length,
    omittedCatalogEventCount: botCatalogEventCount - events.length,
    events,
  };
}
